'use client';

import { useState, useEffect } from 'react';

const MOBILE_BREAKPOINT = 768; // Standard tablet/mobile breakpoint

/**
 * Hook to detect if the user is on a mobile device.
 * Uses both screen width and user agent detection for comprehensive mobile detection.
 * 
 * @returns {boolean} isMobile - true if the user is on a mobile device
 * 
 * @example
 * const isMobile = useIsMobile();
 * 
 * if (isMobile) {
 *   return <MobileComponent />;
 * }
 * return <DesktopComponent />;
 */
export function useIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        // Function to check if device is mobile
        const checkIsMobile = () => {
            // Check screen width
            const isSmallScreen = window.innerWidth < MOBILE_BREAKPOINT;
            
            // Check user agent for mobile devices
            const userAgent = navigator.userAgent || navigator.vendor || (window as Window & { opera?: string }).opera || '';
            const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent);
            
            // Check for touch capability as additional indicator
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            
            // Consider mobile if screen is small OR if it's a mobile user agent with touch
            setIsMobile(isSmallScreen || (isMobileUserAgent && isTouchDevice));
        };

        // Initial check
        checkIsMobile();

        // Listen for resize events
        window.addEventListener('resize', checkIsMobile);

        // Cleanup
        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    return isMobile;
}

/**
 * Hook to get detailed device information.
 * Useful when you need more granular control over device-specific rendering.
 * 
 * @returns {Object} Device information including isMobile, isTablet, isDesktop, and screen dimensions
 */
export function useDeviceInfo() {
    const [deviceInfo, setDeviceInfo] = useState({
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
        screenHeight: typeof window !== 'undefined' ? window.innerHeight : 1080,
        isTouchDevice: false,
    });

    useEffect(() => {
        const checkDevice = () => {
            const width = window.innerWidth;
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            
            const userAgent = navigator.userAgent || navigator.vendor || (window as Window & { opera?: string }).opera || '';
            const isMobileUserAgent = /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile/i.test(userAgent);
            const isTabletUserAgent = /ipad|tablet|playbook|silk/i.test(userAgent);
            
            // Mobile: < 768px or mobile user agent
            const isMobile = width < MOBILE_BREAKPOINT || isMobileUserAgent;
            
            // Tablet: 768px - 1024px or tablet user agent (but not if clearly mobile)
            const isTablet = !isMobile && (width < 1024 || isTabletUserAgent);
            
            // Desktop: everything else
            const isDesktop = !isMobile && !isTablet;

            setDeviceInfo({
                isMobile,
                isTablet,
                isDesktop,
                screenWidth: width,
                screenHeight: window.innerHeight,
                isTouchDevice,
            });
        };

        checkDevice();

        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    return deviceInfo;
}

export default useIsMobile;
